import { useState, useEffect, useCallback, useMemo } from 'react'
import { Trophy, Loader2 } from 'lucide-react'
import { DateRangeFilter, type DateRange } from '@/components/DateRangeFilter'

interface AttractionRow {
  attraction_name: string
  attraction_type: string
  total_scans: number
  unique_guests: number
  revenue: number
  events_appeared: number
}

interface PlanRow {
  plan_name: string
  units_sold: number
  revenue: number
  events_appeared: number
}

function fmt(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`
  return `$${n.toLocaleString()}`
}

function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

type RankDimension = 'revenue' | 'scans' | 'guests' | 'appearances'
const currentYear = new Date().getFullYear().toString()

export function CompanyRankings() {
  const [dateRange, setDateRange] = useState<DateRange>({
    preset: 'ytd', year: currentYear,
    date_from: `${currentYear}-01-01`,
    date_to: new Date().toISOString().slice(0, 10),
  })
  const [attractions, setAttractions] = useState<AttractionRow[]>([])
  const [plans, setPlans] = useState<PlanRow[]>([])
  const [loading, setLoading] = useState(false)
  const [dimension, setDimension] = useState<RankDimension>('revenue')

  const fetchData = useCallback(async (range: DateRange) => {
    setLoading(true)
    const params = new URLSearchParams()
    if (range.year) params.set('year', range.year)
    if (range.date_from) params.set('date_from', range.date_from)
    if (range.date_to) params.set('date_to', range.date_to)
    try {
      const res = await fetch(`/api/dashboard/season?${params}`, { credentials: 'same-origin' })
      if (res.ok) {
        const d = await res.json()
        setAttractions(d.top_attractions || [])
        setPlans(d.top_plans || [])
      }
    } catch { /* */ }
    setLoading(false)
  }, [])

  useEffect(() => {
    const t = setTimeout(() => fetchData(dateRange), 300)
    return () => clearTimeout(t)
  }, [dateRange, fetchData])

  const sortedAttractions = useMemo(() => {
    const sorted = [...attractions]
    if (dimension === 'scans') sorted.sort((a, b) => b.total_scans - a.total_scans)
    else if (dimension === 'guests') sorted.sort((a, b) => b.unique_guests - a.unique_guests)
    else if (dimension === 'appearances') sorted.sort((a, b) => b.events_appeared - a.events_appeared)
    return sorted.slice(0, 20)
  }, [attractions, dimension])

  const DIMS: { id: RankDimension; label: string }[] = [
    { id: 'revenue', label: 'Revenue' },
    { id: 'scans', label: 'Total Scans' },
    { id: 'guests', label: 'Unique Guests' },
    { id: 'appearances', label: 'Events Appeared' },
  ]

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-butler-50">
          <Trophy className="h-5 w-5 text-butler-600" />
        </div>
        <div>
          <h1 className="font-display font-bold text-xl text-ink-dark">Company Rankings</h1>
          <p className="text-sm text-ink-soft">Top attractions and plans across all events</p>
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <DateRangeFilter value={dateRange} onChange={setDateRange} />
        <div className="flex gap-1">
          {DIMS.map(d => (
            <button key={d.id} type="button" onClick={() => setDimension(d.id)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                dimension === d.id ? 'bg-butler-600 text-white' : 'bg-slate-100 text-ink-soft hover:bg-butler-50'
              }`}
            >{d.label}</button>
          ))}
        </div>
      </div>

      {loading && <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-butler-600" /></div>}

      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section className="mm-card-v3 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100">
              <h3 className="font-display font-semibold text-sm text-ink-dark">Top Attractions</h3>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="py-2 px-3 text-left text-[10px] font-semibold uppercase text-ink-soft w-6">#</th>
                  <th className="py-2 px-3 text-left text-[10px] font-semibold uppercase text-ink-soft">Name</th>
                  <th className="py-2 px-3 text-left text-[10px] font-semibold uppercase text-ink-soft">Type</th>
                  <th className="py-2 px-3 text-right text-[10px] font-semibold uppercase text-ink-soft">Revenue</th>
                  <th className="py-2 px-3 text-right text-[10px] font-semibold uppercase text-ink-soft">Guests</th>
                  <th className="py-2 px-3 text-right text-[10px] font-semibold uppercase text-ink-soft">Events</th>
                </tr>
              </thead>
              <tbody>
                {sortedAttractions.map((a, i) => (
                  <tr key={a.attraction_name} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="py-2 px-3 mm-data text-xs text-ink-hint">{i + 1}</td>
                    <td className="py-2 px-3 text-xs font-medium text-ink-dark truncate max-w-[150px]">{a.attraction_name}</td>
                    <td className="py-2 px-3 text-[10px] text-ink-soft">{a.attraction_type}</td>
                    <td className="py-2 px-3 text-right mm-data text-xs font-medium">{fmt(a.revenue)}</td>
                    <td className="py-2 px-3 text-right mm-data text-xs text-ink-soft">{fmtNum(a.unique_guests)}</td>
                    <td className="py-2 px-3 text-right mm-data text-xs text-ink-soft">{a.events_appeared}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="mm-card-v3 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100">
              <h3 className="font-display font-semibold text-sm text-ink-dark">Top Plans</h3>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="py-2 px-3 text-left text-[10px] font-semibold uppercase text-ink-soft w-6">#</th>
                  <th className="py-2 px-3 text-left text-[10px] font-semibold uppercase text-ink-soft">Plan</th>
                  <th className="py-2 px-3 text-right text-[10px] font-semibold uppercase text-ink-soft">Revenue</th>
                  <th className="py-2 px-3 text-right text-[10px] font-semibold uppercase text-ink-soft">Sold</th>
                  <th className="py-2 px-3 text-right text-[10px] font-semibold uppercase text-ink-soft">Events</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((p, i) => (
                  <tr key={p.plan_name} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="py-2 px-3 mm-data text-xs text-ink-hint">{i + 1}</td>
                    <td className="py-2 px-3 text-xs font-medium text-ink-dark truncate max-w-[180px]">{p.plan_name}</td>
                    <td className="py-2 px-3 text-right mm-data text-xs font-medium">{fmt(p.revenue)}</td>
                    <td className="py-2 px-3 text-right mm-data text-xs text-ink-soft">{fmtNum(p.units_sold)}</td>
                    <td className="py-2 px-3 text-right mm-data text-xs text-ink-soft">{p.events_appeared}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      )}
    </div>
  )
}
